import React, { useState, useEffect } from 'react';
import { ConfettiBackground } from '../components/ThemeEffects';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Check, X, UserPlus, Clock } from '@phosphor-icons/react';
import { EventDropLogo } from '../components/EventDropLogo';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RSVPPage = () => {
  const { guestId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [plusOne, setPlusOne] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/rsvp/${guestId}`); setData(r.data); if (r.data.rsvp_status && r.data.rsvp_status !== 'pending') setSubmitted(true); }
      catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, [guestId]);

  const respond = async (status) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp/${guestId}`, { status, plus_one: plusOne });
      setData(prev => ({ ...prev, rsvp_status: status }));
      setSubmitted(true);
    } catch {}
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#15161B] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/50 border-t-transparent" /></div>;

  if (!data) return (
    <div className="min-h-screen bg-[#15161B] flex items-center justify-center text-center px-6">
      <div><p className="text-2xl font-bold text-white mb-2">Invitation not found</p><p className="text-gray-500">This link may have expired.</p></div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: 'radial-gradient(circle at center, #1a1a2e, #15161B)', fontFamily: 'Inter, sans-serif' }} data-testid="rsvp-page">
      {/* iter197 — Universal back-nav. Slug context comes from the
          invite's data.slug; before the invite resolves we fall
          back to the marketing homepage (the only safe target a
          true cold-start guest can reach). */}
      <div style={{ position: 'absolute', top: 18, left: 18, zIndex: 30, paddingTop: 'env(safe-area-inset-top)' }}>
        <BackButton fallbackHref={data?.slug ? `/event/${data.slug}` : '/'} label="Back" variant="subtle" testId="rsvp-back-btn" />
      </div>
      <ConfettiBackground />
      <div className="max-w-md w-full text-center">
        <EventDropLogo size={48} className="mx-auto mb-6 opacity-70" />

        <p className="text-white/40 text-sm mb-2 uppercase tracking-widest font-semibold">You're Invited</p>
        <h1 className="text-4xl font-black text-white mb-3 tracking-tight" data-testid="rsvp-event-name">{data.event_name}</h1>
        {data.event_date && <p className="text-white/50 text-lg mb-8">{data.event_date}</p>}

        <p className="text-white/60 text-base mb-8">Hi <span className="text-white font-semibold">{data.guest_name}</span>, will you be joining?</p>

        {submitted ? (
          <div className="py-8" style={{ animation: 'fadeIn .4s ease-out' }} data-testid="rsvp-confirmed">
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${data.rsvp_status === 'attending' ? 'bg-[#B89762]/20' : data.rsvp_status === 'maybe' ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
              {data.rsvp_status === 'attending' ? <Check size={40} weight="bold" className="text-[#B89762]" /> : data.rsvp_status === 'maybe' ? <Clock size={40} weight="bold" className="text-amber-400" /> : <X size={40} weight="bold" className="text-red-400" />}
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {data.rsvp_status === 'attending' ? 'See you there!' : data.rsvp_status === 'maybe' ? 'We hope you can make it!' : 'Sorry to miss you'}
            </h2>
            <p className="text-white/40 text-sm mb-6">Your response has been recorded</p>
            {data.rsvp_status === 'attending' && data.slug && (
              <Link to={`/event/${data.slug}/g/${guestId}`}
                className="inline-flex items-center justify-center gap-2 transition-all active:scale-95"
                style={{ minHeight: 64, padding: '0 32px', borderRadius: 999, background: 'linear-gradient(135deg, #B89762 0%, #00D4FF 100%)', color: '#FFFFFF', fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', boxShadow: '0 16px 40px rgba(184,151,98,0.45), 0 0 0 1px rgba(255,255,255,0.08) inset' }}
                data-testid="rsvp-go-dashboard">
                Go to Your Dashboard →
              </Link>
            )}
          </div>
        ) : (
          <div data-testid="rsvp-form">
            {/* Plus one toggle */}
            <label className="flex items-center justify-center gap-3 mb-8 cursor-pointer" onClick={() => setPlusOne(!plusOne)}>
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${plusOne ? 'bg-[#B89762] border-[#B89762]/50' : 'border-white/20'}`}>
                {plusOne && <Check size={14} weight="bold" className="text-white" />}
              </div>
              <span className="text-white/60 text-sm flex items-center gap-1.5"><UserPlus size={16} weight="bold" /> Bringing a plus one</span>
            </label>

            {/* RSVP buttons — Event Visibility: 72px tall, gradient
                primary, solid (not ghost) secondaries so they don't
                disappear in dark venues. */}
            <div className="flex gap-3">
              <button onClick={() => respond('attending')} disabled={submitting}
                className="flex-1 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ minHeight: 72, borderRadius: 20, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', border: 'none', boxShadow: '0 16px 40px rgba(16,185,129,0.45), 0 0 0 1px rgba(255,255,255,0.08) inset', cursor: 'pointer' }}
                data-testid="rsvp-yes">
                <Check size={22} weight="bold" /> Yes
              </button>
              <button onClick={() => respond('maybe')} disabled={submitting}
                className="transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center"
                style={{ minHeight: 72, padding: '0 22px', borderRadius: 20, background: 'rgba(245,158,11,0.18)', color: '#FCD34D', fontSize: 17, fontWeight: 700, border: '2px solid rgba(245,158,11,0.55)', cursor: 'pointer' }}
                data-testid="rsvp-maybe">
                Maybe
              </button>
              <button onClick={() => respond('declined')} disabled={submitting}
                className="flex-1 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ minHeight: 72, borderRadius: 20, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', fontSize: 17, fontWeight: 700, border: '2px solid rgba(255,255,255,0.18)', cursor: 'pointer' }}
                data-testid="rsvp-no">
                <X size={20} weight="bold" /> No
              </button>
            </div>
          </div>
        )}

        <p className="text-white/15 text-xs mt-12">Powered by EventDrop</p>
      </div>

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
};

export default RSVPPage;
